import { audienceOf, type FlagRecord, type Role } from '@tkm/domain'

/**
 * Maulik's rollout control surface.
 *
 * Two things on one screen: every flag with its toggle, and one row per person
 * showing what that person can see today. The second half is the one that gets
 * read - "is Mary about to be overwhelmed" should be answerable in a glance, not
 * by mentally joining a flag list to a role list.
 *
 * This screen draws from the same canSee/audienceOf rules the Worker gates on, so
 * a tile shown here is a route that answers. Hiding a tile is never the security
 * boundary though - the Worker is, and it enforces this independently.
 */

export const ROLE_LABELS: Record<Role, string> = {
  owner: 'Owner',
  admin: 'Admin',
  cataloguer: 'Cataloguer',
  fulfilment: 'Fulfilment',
  system: 'System',
}

export interface Person {
  name: string
  role: Role
  note: string
  visible: string[]
  hidden: string[]
}

export interface RolloutData {
  flags: FlagRecord[]
  people: Person[]
}

export interface RolloutDashboardProps extends RolloutData {
  onToggle?: (key: string, state: FlagRecord['state'], targetRoles: Role[]) => void
  saving?: string | null
}

const STATE_LABELS: Record<FlagRecord['state'], string> = {
  off: 'Off',
  on_for_role: 'On for chosen roles',
  on: 'On for everyone',
}

function FlagRow({ flag, onToggle, saving }: { flag: FlagRecord } & Pick<RolloutDashboardProps, 'onToggle' | 'saving'>) {
  const audience = audienceOf(flag)
  const busy = saving === flag.key
  return (
    <li className={`flag flag--${flag.state}`} data-flag={flag.key} data-state={flag.state}>
      <div className="flag__head">
        <h3>{flag.key.replace(/_/g, ' ')}</h3>
        <span className={`pill pill--${flag.state}`}>{STATE_LABELS[flag.state]}</span>
      </div>
      <p className="flag__description">{flag.description}</p>
      {flag.notes ? <p className="flag__notes">{flag.notes}</p> : null}
      <p className="flag__audience">
        {audience.length === 0
          ? 'Nobody can see this yet.'
          : `Visible to: ${audience.map((r) => ROLE_LABELS[r]).join(', ')}`}
      </p>
      <div className="flag__controls">
        {(['off', 'on_for_role', 'on'] as const).map((state) => (
          <button
            key={state}
            type="button"
            disabled={busy}
            aria-pressed={flag.state === state}
            onClick={() => onToggle?.(flag.key, state, state === 'on_for_role' ? flag.targetRoles : [])}
          >
            {STATE_LABELS[state]}
          </button>
        ))}
      </div>
      {flag.state === 'on_for_role' ? (
        <div className="flag__roles">
          {(Object.keys(ROLE_LABELS) as Role[]).map((role) => {
            const on = flag.targetRoles.includes(role)
            return (
              <label key={role}>
                <input
                  type="checkbox"
                  checked={on}
                  disabled={busy}
                  onChange={() =>
                    onToggle?.(
                      flag.key,
                      'on_for_role',
                      on ? flag.targetRoles.filter((r) => r !== role) : [...flag.targetRoles, role],
                    )
                  }
                />
                {ROLE_LABELS[role]}
              </label>
            )
          })}
        </div>
      ) : null}
    </li>
  )
}

function PersonCard({ person }: { person: Person }) {
  return (
    <li className="person" data-person={person.name}>
      <div className="person__head">
        <h3>{person.name}</h3>
        <span className="pill">{ROLE_LABELS[person.role]}</span>
      </div>
      <p className="muted">{person.note}</p>
      <p className="person__count">
        {person.visible.length} of {person.visible.length + person.hidden.length} extras switched on
      </p>
      {person.visible.length === 0 ? (
        <p className="person__none">Core loop only - the gentlest possible starting point.</p>
      ) : (
        <ul className="person__visible">
          {person.visible.map((key) => (
            <li key={key}>{key.replace(/_/g, ' ')}</li>
          ))}
        </ul>
      )}
      {person.hidden.length > 0 ? (
        <details className="person__hidden">
          <summary>{person.hidden.length} still hidden</summary>
          <ul>
            {person.hidden.map((key) => (
              <li key={key}>{key.replace(/_/g, ' ')}</li>
            ))}
          </ul>
        </details>
      ) : null}
    </li>
  )
}

export function RolloutDashboard({ flags, people, onToggle, saving }: RolloutDashboardProps) {
  return (
    <main className="shell rollout">
      {/* h2, not h1: the station shell already owns the page's one h1. Two h1s
          is an accessibility defect and it broke the /rollout e2e assertion. */}
      <h2>Staged rollout</h2>
      <p className="muted">
        Everything here is built. Switching one on is a deliberate act, one at a time, so the switch from
        the old system stays gentle.
      </p>

      <section className="card">
        <h2>Who sees what today</h2>
        {people.length === 0 ? (
          <p className="muted">No people configured.</p>
        ) : (
          <ul className="people">
            {people.map((p) => (
              <PersonCard key={p.name} person={p} />
            ))}
          </ul>
        )}
      </section>

      <section className="card">
        <h2>Flags</h2>
        {flags.length === 0 ? (
          <p className="muted">No flags seeded.</p>
        ) : (
          <ul className="flags">
            {flags.map((f) => (
              <FlagRow key={f.key} flag={f} onToggle={onToggle} saving={saving} />
            ))}
          </ul>
        )}
      </section>
    </main>
  )
}
